import { Box, Button, Dialog, Flex, Text, TextField } from '@radix-ui/themes'
import { RxExclamationTriangle } from '@react-icons/all-files/rx/RxExclamationTriangle'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useTranslation } from '@disco/i18n/use-translation'

import type { ExternalIdentifier } from 'types/writer'

import { DiscoCallout } from 'components/ds'

import {
  buildExternalIdentifiers,
  formatIPIDisplay,
  formatISNIDisplay,
  getIdentifierValue,
  getIPIValidationError,
  getISNIValidationError,
  padIPI,
  parseIPIInput,
  parseISNIInput,
} from '../../utils/identifier-validation'
import InfoPopover from '../info-popover'
import { parseRightsHolderApiError } from '../../utils/validation'

import { useSelector } from 'react-redux'
import { getConstanceSettings } from '@disco/state-management/selectors/user'

export interface BaseDialogProps {
  /** Modal heading text */
  heading: string
  /** Input field label for the name field */
  nameFieldLabel: string
  /** Initial name value for editing or pre-populating from search */
  initialName?: string
  /** Initial external identifiers for editing */
  initialIdentifiers?: ExternalIdentifier[]
  /** Helper text to display below heading (optional) */
  helperText?: string
  /** Save button label */
  saveButtonLabel?: string
  /** Callback when form is submitted with valid data */
  onSave: (
    name: string,
    externalIdentifiers: ExternalIdentifier[] | undefined,
  ) => Promise<void>
  /** Whether the modal is open */
  open: boolean
  /** Whether this is edit mode (disables save if unchanged). Defaults to false for create mode. */
  isEditMode?: boolean
  /** Warning message to display (e.g., duplicate name warning). Blocks save when present. */
  warningMessage?: string | null
  /** Callback when the name input value changes. Used for real-time validation. */
  onNameChange?: (_value: string) => void
  /** Whether to show IPI field */
  showIPI?: boolean
  /** Whether to show ISNI field */
  showISNI?: boolean
}

/**
 * BaseDialog - A dialog for creating/editing entities with IPI/ISNI identifiers
 *
 * Features:
 * - Name field with validation
 * - Optional IPI field with formatting and validation
 * - Optional ISNI field with formatting and validation
 * - Loading state during save
 * - Navigation on cancel/success
 * - Radix Themes Dialog component
 *
 * @param props - Component props
 * @returns BaseDialog component
 */
export function BaseDialog({
  heading,
  nameFieldLabel,
  initialName = '',
  initialIdentifiers = [],
  helperText,
  saveButtonLabel = 'Save',
  onSave,
  open,
  isEditMode = false,
  warningMessage,
  onNameChange,
  showIPI = false,
  showISNI = false,
}: BaseDialogProps) {
  const navigate = useNavigate()
  const { t } = useTranslation('rights')

  //Feature flag check - to be removed after release as part of SLAM-1563
  const constanceSettings = useSelector(getConstanceSettings)
  const rightsNewModelEnabled =
    constanceSettings?.TRACK_RIGHTS_HOLDERS_WRITE_TO_NEW_MODELS || false

  // Extract initial values from identifiers
  const initialIPI = getIdentifierValue(initialIdentifiers, 'ipi') || ''
  const initialISNI = getIdentifierValue(initialIdentifiers, 'isni') || ''

  const [name, setName] = useState(initialName)
  const [ipi, setIpi] = useState(formatIPIDisplay(initialIPI))
  const [isni, setIsni] = useState(formatISNIDisplay(initialISNI))
  const [isSaving, setIsSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [ipiError, setIpiError] = useState<string | null>(null)
  const [isniError, setIsniError] = useState<string | null>(null)

  // Update form when initial values change
  useEffect(() => {
    setName(initialName)
    setIpi(formatIPIDisplay(initialIPI))
    setIsni(formatISNIDisplay(initialISNI))
    setIpiError(null)
    setIsniError(null)
  }, [initialName, initialIPI, initialISNI])

  // Check if form has changed from initial values
  const hasChanged = useMemo(() => {
    const nameChanged = name.trim() !== initialName.trim()
    const ipiChanged = showIPI && parseIPIInput(ipi) !== initialIPI
    const isniChanged = showISNI && parseISNIInput(isni) !== initialISNI
    return nameChanged || ipiChanged || isniChanged
  }, [name, ipi, isni, initialName, initialIPI, initialISNI, showIPI, showISNI])

  // Handle dialog open/close state changes
  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        navigate(-1)
      } else {
        // Reset form when modal opens
        setName(initialName)
        setIpi(formatIPIDisplay(initialIPI))
        setIsni(formatISNIDisplay(initialISNI))
        setNameError(null)
        setSaveError(null)
        setIpiError(null)
        setIsniError(null)
      }
    },
    [navigate, initialName, initialIPI, initialISNI],
  )

  const handleSave = useCallback(async () => {
    // Validate name
    const trimmedName = name.trim()
    if (!trimmedName) {
      setNameError('This field is required')
      return
    }

    const nextIpiError = showIPI ? getIPIValidationError(ipi, t) : null
    const nextIsniError = showISNI ? getISNIValidationError(isni, t) : null
    setIpiError(nextIpiError)
    setIsniError(nextIsniError)
    if (nextIpiError || nextIsniError) {
      return
    }

    try {
      setIsSaving(true)
      setNameError(null)
      setSaveError(null)

      // Build external identifiers array
      const externalIdentifiers = buildExternalIdentifiers(
        showIPI ? padIPI(ipi) : undefined,
        showISNI ? isni : undefined,
      )

      await onSave(trimmedName, externalIdentifiers)
      navigate(-1)
    } catch (err) {
      setSaveError(parseRightsHolderApiError(err))
    } finally {
      setIsSaving(false)
    }
  }, [name, ipi, isni, showIPI, showISNI, onSave, navigate, t])

  const handleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value
      setName(newValue)
      if (nameError) {
        setNameError(null)
      }
      if (saveError) {
        setSaveError(null)
      }
      onNameChange?.(newValue)
    },
    [nameError, saveError, onNameChange],
  )

  const handleIPIChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value
      const rawValue = parseIPIInput(inputValue)
      setIpi(formatIPIDisplay(rawValue))
      setIpiError(null)
      if (saveError) {
        setSaveError(null)
      }
    },
    [saveError],
  )

  const handleISNIChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsni(e.target.value)
      setIsniError(null)
      if (saveError) {
        setSaveError(null)
      }
    },
    [saveError],
  )

  const handleISNIBlur = useCallback(() => {
    setIsni(prev => formatISNIDisplay(parseISNIInput(prev)))
  }, [])

  // Disable save conditions
  const isEmpty = !name.trim()
  const isUnchanged = isEditMode && !hasChanged
  // Only show duplicate warning when name has changed from initial value
  const nameHasChanged =
    name.trim().toLowerCase() !== initialName.trim().toLowerCase()
  const hasDuplicateWarning = Boolean(warningMessage) && nameHasChanged
  const isSaveDisabled = isSaving || isEmpty || isUnchanged

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content maxWidth="400px">
        <Box>
          <Dialog.Title>{heading}</Dialog.Title>

          {helperText && (
            <Dialog.Description color="gray" mb="4" size="2">
              {helperText}
            </Dialog.Description>
          )}

          {/* Name field */}
          <Box mb="4">
            <Text as="label" size="2" weight="medium" mb="1">
              {nameFieldLabel}
            </Text>
            <TextField.Root
              value={name}
              onChange={handleNameChange}
              disabled={isSaving}
              size="2"
              required
            />
            {nameError && (
              <Text size="2" color="red" mt="1">
                {nameError}
              </Text>
            )}
            {hasDuplicateWarning && !nameError && (
              <Box mt="2">
                <DiscoCallout
                  type="warning-important"
                  icon={<RxExclamationTriangle />}
                >
                  {warningMessage}
                </DiscoCallout>
              </Box>
            )}
          </Box>

          {/* IPI and ISNI fields */}
          {rightsNewModelEnabled && (showIPI || showISNI) && (
            <Flex gap="2" mb="4">
              {showIPI && (
                <Flex flexGrow="1" direction="column">
                  <Flex align="center" gap="1">
                    <Text
                      as="label"
                      htmlFor="base-dialog-ipi"
                      size="2"
                      weight="medium"
                    >
                      IPI
                    </Text>
                    <InfoPopover
                      label={t(
                        'rights:moreInfoAbout',
                        'More info about {{field}}',
                        { field: 'IPI' },
                      )}
                      title={t(
                        'rights:ipiInfoTitle',
                        'Interested Party Information Number (IPI)',
                      )}
                    >
                      {t(
                        'rights:ipiInfoBody',
                        'An 11-digit identifier assigned to music rights holders such as composers, lyricists, and publishers. If you enter fewer than 11 digits, leading zeros will be added automatically.',
                      )}
                    </InfoPopover>
                  </Flex>
                  <TextField.Root
                    id="base-dialog-ipi"
                    value={ipi}
                    onChange={handleIPIChange}
                    placeholder="00000000000"
                    disabled={isSaving}
                    size="2"
                  />
                  {ipiError && (
                    <Text size="1" color="red" mt="1">
                      {ipiError}
                    </Text>
                  )}
                </Flex>
              )}
              {showISNI && (
                <Flex flexGrow="1" direction="column">
                  <Flex align="center" gap="1">
                    <Text
                      as="label"
                      htmlFor="base-dialog-isni"
                      size="2"
                      weight="medium"
                    >
                      ISNI
                    </Text>
                    <InfoPopover
                      label={t(
                        'rights:moreInfoAbout',
                        'More info about {{field}}',
                        { field: 'ISNI' },
                      )}
                      title={t(
                        'rights:isniInfoTitle',
                        'International Standard Name Identifier (ISNI)',
                      )}
                    >
                      {t(
                        'rights:isniInfoBody',
                        'A 16-character code that uniquely identifies a contributor such as a composer, performer, writer, or organization. Enter 15 digits plus a final digit or X. Spaces are added automatically.',
                      )}
                    </InfoPopover>
                  </Flex>
                  <TextField.Root
                    id="base-dialog-isni"
                    value={isni}
                    onChange={handleISNIChange}
                    onBlur={handleISNIBlur}
                    placeholder="0000 0000 0000 0000"
                    disabled={isSaving}
                    size="2"
                  />
                  {isniError && (
                    <Text size="1" color="red" mt="1">
                      {isniError}
                    </Text>
                  )}
                </Flex>
              )}
            </Flex>
          )}

          {saveError && (
            <Box mb="3">
              <DiscoCallout type="error" icon={<RxExclamationTriangle />}>
                {saveError}
              </DiscoCallout>
            </Box>
          )}

          <Flex gap="2" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" disabled={isSaving}>
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              onClick={handleSave}
              disabled={isSaveDisabled}
              loading={isSaving}
            >
              {saveButtonLabel}
            </Button>
          </Flex>
        </Box>
      </Dialog.Content>
    </Dialog.Root>
  )
}
